import React, { useEffect, useState, useRef } from 'react';
import './CustomCircularProgress.css';

const CustomCircularProgress = ({ size, progressColor, trackColor, targetValue, figureText }) => {
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;

  const [value, setValue] = useState(0);
  const [isIntersecting, setIsIntersecting] = useState(false);

  const progressRef = useRef(null);

  // Rounds a number to the nearest decimal precision
  const roundToPrecision = (num, precision = 0) => {
    const factor = Math.pow(10, precision);
    return Math.round(num * factor) / factor;
  };

  // Animation setup
  useEffect(() => {
    let startValue = value;
    let startTime = null;
    const duration = 1750;

    const animate = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
      }

      const elapsedTime = timestamp - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      const easedProgress = customEase(progress);

      const animatedValue = startValue + easedProgress * (targetValue - startValue);
      setValue(animatedValue);

      if (elapsedTime < duration && animatedValue !== targetValue && isIntersecting) {
        requestAnimationFrame(animate);
      }
    };

    const customEase = (t) => 1 - Math.pow(1 - t, 2);

    if (isIntersecting) {
      requestAnimationFrame(animate);
    }

    return () => {
      startTime = null;
    };
  }, [targetValue, isIntersecting]);

  // Intersection Observer setup
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsIntersecting(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (progressRef.current) {
      observer.observe(progressRef.current);
    }

    return () => {
      if (progressRef.current) {
        observer.unobserve(progressRef.current);
      }
    };
  }, []);

  const offset = circumference - (value / 100) * circumference;

  const trackStyle = {
    fill: 'none',
    stroke: trackColor,
    strokeWidth: 9,
  };

  const progressStyle = {
    fill: 'none',
    stroke: progressColor,
    strokeWidth: 9,
    strokeLinecap: 'square',
    strokeDasharray: `${circumference} ${circumference}`,
    strokeDashoffset: offset,
  };

  const textStyle = {
    fontSize: 54,
    fontWeight: 600,
    letterSpacing: '-.15rem',
    fill: progressColor,
    dominantBaseline: 'middle',
    textAnchor: 'middle',
    transform: `translate(50%, 54%) rotate(345deg) translate(0, 0)`,
  };

  const percentageStyle = {
    fontSize: 30,
    fontWeight: 200,
    fill: progressColor,
    textAnchor: 'start',
    transform: `rotate(345deg) translate(-75px, -20px)`,
  };

  const figureStyle = {
    fontSize: '1.2rem',
    fontWeight: 300,
    color: '#4D4D4D',
    display: 'block',
    marginTop: '10px',
  };

  const percentageTextX = size / 2 + radius + 5;
  const percentageTextY = size / 2 + 20;

  return (
    <div ref={progressRef}>
      {isIntersecting && (
        <>
          <svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size, overflow: 'visible', transform: 'rotate(15deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={radius} style={trackStyle} />
            <circle cx={size / 2} cy={size / 2} r={radius} style={progressStyle} />
            <text className='percentageValue' x="0" y="0" style={textStyle}>
              {roundToPrecision(value, 1)}%
            </text>
          </svg>
          <br />
          <text style={figureStyle} dangerouslySetInnerHTML={{ __html: figureText }} />
        </>
      )}
    </div>
  );
};

export default CustomCircularProgress;